<template>
	<div id="app">
		<router-view />

		<!-- RGPD cookie -->
		<cookie-law buttonText="Ok">
				<div slot="message">
				Nous utilisons des cookies pour vous garantir la meilleure expérience sur notre site. Si vous continuez à utiliser ce dernier, nous considérerons que vous acceptez l'utilisation des cookies.
			</div>
		</cookie-law>

		<!-- Push Notification Permission Popup -->
		<push-notification-popup v-if="showPushPopup" :user="showPushPopup"
			@close="handlePushPopupClose"
			@subscribed="handlePushSubscribed"
		/>

		<!-- Show a global error -->
		<ui-modal v-if="errors.length" @close="errors.shift()" title="Erreur" class="ui-modal--error">{{ errors[0] }}</ui-modal>

		<!-- Confirm -->
		<ui-modal v-if="confirms.length" @close="closeConfirm" class="ui-modal--confirm" :title="confirms[0].title">
			<template slot="default">{{ confirms[0].content }}</template>
			<template slot="buttons">
				<ui-button type="button" class="white-button" @click="closeConfirm(false)">{{ confirms[0].no_text }}</ui-button>
				<ui-button type="button" @click="closeConfirm(true)">{{ confirms[0].yes_text }}</ui-button>
			</template>
		</ui-modal>
	</div>
</template>

<script>
import CookieLaw from 'vue-cookie-law'
import Vue from 'vue'
import { eventBus } from './main'
import { isPushAvailable } from './service'

// Print the error in a standard modal
const errors = []
Vue.prototype.$error = function (err) {
  console.error(err)
  err = err.response && err.response.data && err.response.data.error ? err.response.data.error : (err.message ? err.message : err + '')
  errors.push(err)
}

// Ask a yes/no question, returns a promise that resolve with the answer
const confirms = []
Vue.prototype.$confirm = function (text, yes_text, no_text) {
  let title, content
  if (typeof text === 'string') content = text
  else {
    title = text.title
    content = text.content
  }

  const data = {
    resolve: null,
    title,
    content,
    yes_text: yes_text || 'Oui',
    no_text: no_text || 'Annuler',
  }
  confirms.push(data)
  return new Promise(resolve => data.resolve = resolve)
}

export default {
  data: () => ({
    contact: {
      way: 'mail',
      timing: [],
    },
    errors,
    confirms,
    showPushPopup: null,
  }),
  mounted() {
    // Listen for user login events to potentially show push popup
    eventBus.$on('userChanged', user => {
      if (user && user.registration_step === 100) {
        this.checkPushNotificationPopup(user)
      }
    })
  },
  methods: {
    closeConfirm(res) {
      this.confirms[0].resolve(res)
      this.confirms.shift()
    },
    async checkPushNotificationPopup(user) {
      // Check if push is available
      const pushAvailable = await isPushAvailable()
      if (!pushAvailable) return

      // Don't show if already subscribed
      if (localStorage.getItem('service:push:' + user.id)) {
        console.log('Push available, already subscribed')
        return
      }

      // Don't show if permission already denied
      if (Notification.permission === 'denied') {
        console.log('Notification permission denied')
        return
      }

      // Check if we already asked (wait 7 days before asking again)
      const lastAsked = localStorage.getItem('push:asked:' + user.id)
      if (lastAsked) {
        const daysSinceAsked = (Date.now() - parseInt(lastAsked)) / (1000 * 60 * 60 * 24)
        if (daysSinceAsked < 7) return
      }

      // Wait a bit before showing the popup (better UX)
      setTimeout(() => {
        console.log('Should display notification popup')
        this.showPushPopup = user
      }, 3000)
    },
    handlePushPopupClose(event) {
      if (event === 'cancelled') {
        localStorage.setItem('push:asked:' + this.showPushPopup.id, Date.now().toString())
      }
      this.showPushPopup = null
    },
    handlePushSubscribed(success) {
      if (success) {
        console.log('User subscribed to push notifications')
      }
    },
  },
  metaInfo: {
    title: 'Colocataire, colocation, logement contre services | COOLOC',
    meta: [
      // Search engine
      { name: 'keywords', content: 'Colocataires, colocation, logement contre services, habitat partagé, parents solo, intergénérationnel' },
      { vmid: 'description', name: 'description', content: 'Le premier réseau de colocataires par affinités. Une nouvelle vision de la colocation.' },
      { vmid: 'image', name: 'image', content: 'https://cooloc.com/static/images/cooloc-social.jpg' },
      // Schema.org
      { itemprop: 'name', content: 'COOLOC' },
      { vmid: 'ip:description', itemprop: 'description', content: 'Une nouvelle vision de la co-location.' },
      { vmid: 'ip:image', itemprop: 'image', content: 'https://cooloc.com/static/images/cooloc-social.jpg' },
      // Open Graph general (Facebook, Pinterest & Google+)
      { vmid: 'og:title', property: 'og:title', content: 'COOLOC' },
      { vmid: 'og:description', property: 'og:description', content: 'Une nouvelle vision de la co-location.' },
      { vmid: 'og:image', property: 'og:image', content: 'https://cooloc.com/static/images/cooloc-social.jpg' },
      { vmid: 'og:url', property: 'og:url', content: 'https://cooloc.com/' },
      { property: 'og:site_name', content: 'COOLOC' },
      { property: 'og:type', content: 'website' },
    ],
  },
  components: {
    CookieLaw,
    UiModal: require('@/ui/Modal').default,
    UiButton: require('@/ui/Button').default,
    PushNotificationPopup: require('@/components/common/PushNotificationPopup').default,
  },
}
</script>

<!-- Global SCSS partials (variables etc.) are auto-injected via sass-loader additionalData in build/utils.js -->
<style src="./../static/scss/main" lang="scss"></style>

<style lang="scss">
.Cookie {
	z-index: 9999999999 !important;
}
.ui-modal--error, .ui-modal--confirm {
	p {
		white-space: pre-wrap;
	}
	.simple-popup#popup-info {
		padding: 30px;
	}
}
.ui-modal--confirm .actions {
	margin: 40px -10px 0;
	.button {
		margin: 0 10px;
		flex: 1;
	}
}
</style>
